/**
 * Location Data Service
 *
 * Provides US city/state search functionality via backend API.
 * This approach avoids loading large datasets in the browser, improving
 * initial load time and mobile performance.
 *
 * The backend uses the country-state-city library and returns only the
 * results needed for the current search query.
 */

import { apiRequest } from './api';

// Types
export interface LocationResult {
  city: string;
  stateCode: string;
  stateName: string;
  formatted: string; // "City, ST"
}

export interface USState {
  name: string;
  isoCode: string;
}

/**
 * Combined city + county search result. Cities and counties are returned in
 * the same list so the picker can offer both in a single dropdown — counties
 * sort to the top so a user typing a name that's both ("Washington") sees the
 * county option first.
 */
export interface GeoResult {
  type: 'city' | 'county';
  name: string;
  stateCode: string;
  stateName: string;
  /** For city results, the primary county containing the city (if known). */
  county?: string;
  /** Display string — "City, ST" for cities, "County Name County, ST" for counties. */
  formatted: string;
}

// Cache for states (small, rarely changes)
let usStatesCache: USState[] | null = null;

/**
 * Search cities by query via backend API
 *
 * @param query - Search query (minimum 2 characters)
 * @param maxResults - Maximum number of results to return (default 10)
 * @returns Promise resolving to array of LocationResult objects
 */
export async function searchCities(query: string, maxResults: number = 10): Promise<LocationResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const results = await apiRequest<LocationResult[]>('/api/locations/search', {
      params: {
        q: query,
        limit: maxResults,
      },
    });
    return results;
  } catch (error) {
    console.error('Error searching cities:', error);
    return [];
  }
}

/**
 * Search cities AND counties in one shot. Used by the service-area picker so
 * users can pick a whole county (which then matches every city inside it).
 *
 * Pass `stateCode` to restrict results to a single state without polluting the
 * visible search text — the state-first picker keeps the selected state in a
 * separate control and forwards it here.
 */
export async function searchGeo(
  query: string,
  maxResults: number = 10,
  stateCode?: string,
): Promise<GeoResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const params: Record<string, string | number> = {
      q: query,
      limit: maxResults,
    };
    if (stateCode) params.state = stateCode;
    const results = await apiRequest<GeoResult[]>('/api/locations/geo/search', {
      params,
    });
    return results;
  } catch (error) {
    console.error('Error searching geo:', error);
    return [];
  }
}

/**
 * A reverse-geocoded city, with how far the coordinate was from its centre.
 * Structurally a {@link GeoResult}, so it drops straight into `onSelect`.
 */
export interface ReverseGeoMatch extends GeoResult {
  type: 'city';
  distanceKm: number;
}

export interface ReverseGeoResult {
  /** Nearest matchable city, or null when the point is outside US coverage. */
  match: ReverseGeoMatch | null;
  /** Ranked next-nearest cities, for correcting a fix that landed next door. */
  alternatives: ReverseGeoMatch[];
  /** The county containing `match`, offered as a wider service area. */
  county: GeoResult | null;
}

/**
 * Resolve a device coordinate to a service area.
 *
 * The backend names the point with Google (server-side key) and then ratifies
 * that name against the same county dataset that backs `searchGeo`, so anything
 * returned here matches exactly like a typed entry. No geocoding key reaches
 * the bundle. See `src/docs/SERVICE_AREA_LOCATION.md` §2.
 *
 * Throws on transport failure so the caller can distinguish "we could not ask"
 * from "there is nothing near you" (`match === null`).
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number,
  maxAlternatives = 4,
): Promise<ReverseGeoResult> {
  return apiRequest<ReverseGeoResult>('/api/locations/reverse', {
    params: {
      lat: latitude,
      lng: longitude,
      limit: maxAlternatives,
    },
  });
}

export interface ForwardGeoResult {
  latitude: number;
  longitude: number;
  formatted: string;
}

/**
 * Coordinates for an existing service-area string, used only to CENTRE the map
 * pin-drop when it is opened without a device fix. Answers from the local
 * dataset, so it costs no Google call. Resolves to null when the string cannot
 * be placed — the caller then opens a wide default view.
 */
export async function geocodeServiceArea(query: string): Promise<ForwardGeoResult | null> {
  if (!query || query.trim().length < 3) return null;
  try {
    const { result } = await apiRequest<{ result: ForwardGeoResult | null }>(
      '/api/locations/geocode',
      { params: { q: query.trim() } },
    );
    return result;
  } catch (error) {
    console.error('Error geocoding service area:', error);
    return null;
  }
}

/**
 * Get all US states via backend API
 * Results are cached after first fetch.
 */
export async function getUSStates(): Promise<USState[]> {
  if (usStatesCache) {
    return usStatesCache;
  }

  try {
    const states = await apiRequest<USState[]>('/api/locations/states');
    usStatesCache = states;
    return states;
  } catch (error) {
    console.error('Error fetching states:', error);
    return [];
  }
}

/**
 * Validate a city/state string via backend API
 *
 * @param locationString - Location string (e.g., "Boise, ID")
 * @returns Promise resolving to validation result
 */
export async function validateCityState(locationString: string): Promise<{
  valid: boolean;
  location?: LocationResult;
  error?: string;
}> {
  try {
    const result = await apiRequest<{
      valid: boolean;
      location?: LocationResult;
      error?: string;
    }>('/api/locations/validate', {
      params: {
        location: locationString,
      },
    });
    return result;
  } catch (error) {
    console.error('Error validating location:', error);
    return {
      valid: false,
      error: 'Failed to validate location',
    };
  }
}

/**
 * Find a specific city by name and state code
 * Uses the validate endpoint under the hood
 *
 * @param cityName - City name
 * @param stateCode - State code (e.g., "ID")
 * @returns Promise resolving to LocationResult if found, null otherwise
 */
export async function findCity(cityName: string, stateCode: string): Promise<LocationResult | null> {
  const locationString = `${cityName}, ${stateCode}`;
  const result = await validateCityState(locationString);
  return result.valid && result.location ? result.location : null;
}

/**
 * Check if a state code is valid
 *
 * @param stateCode - State code to validate (e.g., "ID", "CA")
 * @returns Promise resolving to true if valid US state code
 */
export async function isValidStateCode(stateCode: string): Promise<boolean> {
  const states = await getUSStates();
  const normalized = stateCode.toUpperCase().trim();
  return states.some(s => s.isoCode === normalized);
}

/**
 * Get state name from state code
 *
 * @param stateCode - State code (e.g., "ID")
 * @returns Promise resolving to state name or null if not found
 */
export async function getStateName(stateCode: string): Promise<string | null> {
  const states = await getUSStates();
  const normalized = stateCode.toUpperCase().trim();
  const state = states.find(s => s.isoCode === normalized);
  return state?.name || null;
}

/**
 * Get state code from state name
 *
 * @param stateName - State name (e.g., "Idaho")
 * @returns Promise resolving to state code or null if not found
 */
export async function getStateCode(stateName: string): Promise<string | null> {
  const states = await getUSStates();
  const normalized = stateName.toLowerCase().trim();
  const state = states.find(s => s.name.toLowerCase() === normalized);
  return state?.isoCode || null;
}
